// Import the shared Supabase client instead of creating a new one
import { supabase } from '@/lib/supabaseClient';
export { supabase };

// טיפוסים
export type UserProfile = {
  id: number;
  user_id: string;
  display_name: string;
  role: string;
  org_id: number | null;
  created_at: string;
  email?: string;
  permissions_json: {
    view_all_leads?: boolean;
    edit_all_leads?: boolean;
    can_invite_users?: boolean;
    [key: string]: any;
  };
};

export type ScreenPermission = {
  id: number;
  user_id: string;
  screen_id: string;
  can_view: boolean;
  can_edit: boolean;
  created_at: string;
};

export type Lead = {
  id: number;
  user_id: string;
  org_id: number | null;
  name: string;
  email: string;
  phone: string;
  status: string;
  source: string;
  created_at: string;
  updated_at: string;
  notes: string | null;
  assigned_to: string | null;
};

export type Organization = {
  id: string;
  name: string;
  created_at: string;
  user_id: string;
  org_details?: any;
  org_logo?: string;
  currency?: string;
};

// פונקציות מסייעות

// אימות משתמשים
export const authService = {
  // התחברות עם אימייל וסיסמה
  async login(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    
    // טיפול בשגיאות אימות אימייל
    if (error && error.message === 'Email not confirmed') {
      console.log('Email not confirmed. Trying to confirm automatically...');
      
      try {
        // מציאת המשתמש לפי המייל
        const { data: { users }, error: searchError } = await supabase.auth.admin.listUsers();
        
        if (searchError) {
          throw searchError;
        }
        
        const user = users.find((u: any) => u.email === email);
        
        if (!user) {
          console.error(`User with email ${email} not found`);
          throw new Error(`User with email ${email} not found`);
        }
        
        // ניסיון לאמת את האימייל אוטומטית
        const { data: adminData, error: adminError } = await supabase.auth.admin.updateUserById(
          user.id,
          { email_confirm: true }
        );
        
        if (!adminError) {
          // ניסיון התחברות מחדש לאחר האימות
          return await supabase.auth.signInWithPassword({ email, password });
        }
      } catch (confirmError) {
        console.error('Failed to auto-confirm email:', confirmError);
      }
      
      // אם האימות האוטומטי נכשל, החזר את השגיאה המקורית עם הודעה מותאמת
      return { 
        data: null, 
        error: { 
          ...error, 
          message: 'האימייל לא אומת. נסה לבדוק את תיבת הדואר שלך או ליצור קשר עם מנהל המערכת' 
        }
      };
    }
    
    return { data, error };
  },

  // יציאה מהמערכת
  async logout() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // הרשמה עם אימייל וסיסמה
  async register(email: string, password: string) {
    const { data, error } = await supabase.auth.signUp({ email, password });
    return { data, error };
  },

  // קבלת המשתמש הנוכחי
  async getCurrentUser() {
    const { data, error } = await supabase.auth.getUser();
    return { user: data.user, error };
  },

  // קבלת סשן
  async getSession() {
    return await supabase.auth.getSession();
  },

  // איפוס סיסמה
  async resetPassword(email: string) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email);
    return { data, error };
  },

  // עדכון סיסמה
  async updatePassword(password: string) {
    return await supabase.auth.updateUser({ password });
  }
};

// שירות פרופיל משתמש
export const profileService = {
  // יצירת פרופיל משתמש חדש
  async createProfile(userId: string, displayName: string, role = 'user', orgId: number | null = null) {
    const { data, error } = await supabase
      .from('user_profiles')
      .insert({
        user_id: userId,
        display_name: displayName,
        role: role,
        org_id: orgId,
        permissions_json: {
          view_all_leads: role === 'admin',
          edit_all_leads: role === 'admin',
          can_invite_users: role === 'admin',
        },
      })
      .select()
      .single();

    return { data, error };
  },

  // קבלת פרופיל המשתמש הנוכחי
  async getCurrentProfile() {
    const { user, error: userError } = await authService.getCurrentUser();
    if (userError || !user) {
      return { data: null, error: userError };
    }

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', user.id)
      .single();

    return { data, error };
  },

  // עדכון פרופיל המשתמש
  async updateProfile(profileData: Partial<UserProfile>) {
    const { user, error: userError } = await authService.getCurrentUser();
    if (userError || !user) {
      return { data: null, error: userError };
    }

    const { data, error } = await supabase
      .from('user_profiles')
      .update(profileData)
      .eq('user_id', user.id)
      .select()
      .single();

    return { data, error };
  },
  
  // קבלת רשימת משתמשי משנה
  async getSubUsers() {
    const { user, error: userError } = await authService.getCurrentUser();
    if (userError || !user) {
      return { data: null, error: userError };
    }

    // קבלת הפרופיל של המשתמש הנוכחי
    const { data: currentProfile, error: profileError } = await this.getCurrentProfile();
    if (profileError || !currentProfile) {
      return { data: null, error: profileError };
    }

    // משתמשים השייכים לאותו ארגון (אם המשתמש שייך לארגון)
    let query = supabase.from('user_profiles').select('*');
    
    if (currentProfile.org_id) {
      // אם המשתמש שייך לארגון, מביאים את כל המשתמשים באותו ארגון
      query = query.eq('org_id', currentProfile.org_id);
    } else {
      // אם המשתמש לא שייך לארגון, אז זה כנראה משתמש עצמאי
      // במקרה כזה מביאים רק משתמשים שהוא יצר (נניח שהם יהיו מקושרים אליו)
      // זו רק דוגמה - יש להתאים לפי הלוגיקה העסקית הרצויה
      query = query.eq('created_by', user.id);
    }

    const { data, error } = await query;
    return { data, error };
  },
  
  // הזמנת משתמש חדש (תת-משתמש)
  async inviteUser({ 
    email, 
    display_name, 
    role = 'user', 
    permissions_json = {}, 
    org_id = null,
    screen_permissions = []
  }: {
    email: string; 
    display_name: string;
    role?: string;
    permissions_json?: Record<string, any>;
    org_id?: number | null;
    screen_permissions?: Array<{
      screen_id: string;
      can_view: boolean;
      can_edit: boolean;
    }>;
  }) {
    // קבלת המשתמש הנוכחי
    const { user, error: userError } = await authService.getCurrentUser();
    if (userError || !user) {
      return { data: null, error: userError };
    }

    // יצירת משתמש חדש
    const { data: newUser, error: registerError } = await supabase.auth.admin.createUser({
      email,
      password: Math.random().toString(36).slice(-10), // סיסמה אקראית
      email_confirm: true
    });

    if (registerError || !newUser.user) {
      return { data: null, error: registerError };
    }

    // יצירת פרופיל למשתמש החדש
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .insert({
        user_id: newUser.user.id,
        display_name,
        role,
        org_id,
        permissions_json,
        created_by: user.id // מי יצר את המשתמש
      })
      .select()
      .single();

    if (profileError) {
      // אם נכשל ביצירת הפרופיל, יש למחוק את המשתמש שנוצר
      await supabase.auth.admin.deleteUser(newUser.user.id);
      return { data: null, error: profileError };
    }

    // יצירת הרשאות מסך למשתמש
    if (screen_permissions && screen_permissions.length > 0) {
      const permissionsToInsert = screen_permissions
        .filter(p => p.can_view || p.can_edit) // רק הרשאות שיש בהן גישה כלשהי
        .map(p => ({
          user_id: newUser.user.id,
          screen_id: p.screen_id,
          can_view: p.can_view,
          can_edit: p.can_edit
        }));

      if (permissionsToInsert.length > 0) {
        const { error: permError } = await supabase
          .from('screen_permissions')
          .insert(permissionsToInsert);
        
        if (permError) {
          console.error('Error creating screen permissions:', permError);
        }
      }
    }

    // שליחת אימייל עם קישור לאיפוס סיסמה
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email);

    return { 
      data: { 
        user: newUser.user, 
        profile 
      }, 
      error: resetError 
    };
  },
  
  // קבלת הרשאות מסך עבור משתמש
  async getScreenPermissions(userId?: string) {
    // אם לא הועבר משתמש ספציפי, נשתמש במשתמש הנוכחי
    let targetUserId = userId;
    
    if (!targetUserId) {
      const { user, error: userError } = await authService.getCurrentUser();
      if (userError || !user) {
        return { data: null, error: userError };
      }
      targetUserId = user.id;
    }

    const { data, error } = await supabase
      .from('screen_permissions')
      .select('*')
      .eq('user_id', targetUserId);

    return { data, error };
  }
};

// שירות בדיקת הרשאות
export const permissionService = {
  // בדיקה אם למשתמש יש הרשאה מסוימת
  async hasPermission(permissionKey: string) {
    const { data: profile, error } = await profileService.getCurrentProfile();
    if (error || !profile) {
      return false;
    }

    return !!(profile as any).permissions_json && !!(profile as any).permissions_json[permissionKey];
  },

  // בדיקה אם למשתמש יש הרשאה לגשת למסך מסוים
  async canAccessScreen(screenId: string, requireEdit = false) {
    const { data: permissions, error } = await profileService.getScreenPermissions();
    if (error || !permissions) {
      return false;
    }

    const screenPermission = permissions.find(p => p.screen_id === screenId);
    if (!screenPermission) {
      return false;
    }

    return requireEdit ? screenPermission.can_edit : screenPermission.can_view;
  },
};

// שירות ניהול לידים
export const leadService = {
  // קבלת כל הלידים
  async getLeads() {
    const { user, error: userError } = await authService.getCurrentUser();
    if (userError || !user) {
      return { data: null, error: userError };
    }

    // בדיקה אם למשתמש יש הרשאת צפייה בכל הלידים
    const canViewAll = await permissionService.hasPermission('view_all_leads');
    
    let query = supabase.from('leads').select('*');
    
    // אם אין הרשאה לצפות בכל הלידים, מביאים רק את הלידים של המשתמש
    if (!canViewAll) {
      query = query.eq('user_id', user.id).or(`assigned_to.eq.${user.id}`);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    return { data, error };
  },

  // קבלת ליד לפי מזהה
  async getLeadById(id: number) {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('id', id)
      .single();

    return { data, error };
  },

  // יצירת ליד חדש
  async createLead(leadData: Partial<Lead>) {
    const { user, error: userError } = await authService.getCurrentUser();
    if (userError || !user) {
      return { data: null, error: userError };
    }

    // קבלת הפרופיל של המשתמש (כדי לקבל את ה-org_id)
    const { data: profile, error: profileError } = await profileService.getCurrentProfile();
    if (profileError) {
      return { data: null, error: profileError };
    }

    const { data, error } = await supabase
      .from('leads')
      .insert({
        ...leadData,
        user_id: user.id,
        org_id: profile?.org_id || null,
      })
      .select()
      .single();

    return { data, error };
  },

  // עדכון ליד
  async updateLead(id: number, leadData: Partial<Lead>) {
    const { user, error: userError } = await authService.getCurrentUser();
    if (userError || !user) {
      return { data: null, error: userError };
    }

    // בדיקה אם למשתמש יש הרשאת עריכה לכל הלידים
    const canEditAll = await permissionService.hasPermission('edit_all_leads');
    
    // אם אין הרשאת עריכה כללית, נבדוק שהליד שייך למשתמש
    if (!canEditAll) {
      const { data: lead, error: leadError } = await this.getLeadById(id);
      if (leadError || !lead) {
        return { data: null, error: leadError || new Error('Lead not found') };
      }
      
      if (lead.user_id !== user.id && lead.assigned_to !== user.id) {
        return { data: null, error: new Error('You do not have permission to edit this lead') };
      }
    }

    const { data, error } = await supabase
      .from('leads')
      .update(leadData)
      .eq('id', id)
      .select()
      .single();

    return { data, error };
  },

  // מחיקת ליד
  async deleteLead(id: number) {
    const { user, error: userError } = await authService.getCurrentUser();
    if (userError || !user) {
      return { error: userError };
    }

    // בדיקה אם למשתמש יש הרשאת עריכה לכל הלידים
    const canEditAll = await permissionService.hasPermission('edit_all_leads');
    
    // אם אין הרשאת עריכה כללית, נבדוק שהליד שייך למשתמש
    if (!canEditAll) {
      const { data: lead, error: leadError } = await this.getLeadById(id);
      if (leadError || !lead) {
        return { error: leadError || new Error('Lead not found') };
      }
      
      if (lead.user_id !== user.id) {
        return { error: new Error('You do not have permission to delete this lead') };
      }
    }

    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', id);

    return { error };
  },
};

// שירות ניהול ארגונים
export const organizationService = {
  async getOrganization(orgId: string) {
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", orgId)
        .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching organization:", error);
      return null;
    }
  },

  async updateOrganization(orgId: string, updates: Partial<Organization>) {
    try {
      const { data, error } = await supabase
        .from("organizations")
        .update(updates)
        .eq("id", orgId)
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error updating organization:", error);
      return null;
    }
  },

  async updateCurrency(orgId: string, currency: string) {
    try {
      const { data, error } = await supabase
        .from("organizations")
        .update({ currency })
        .eq("id", orgId)
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error updating organization currency:", error);
      return null;
    }
  },

  // יצירת ארגון חדש
  async createOrganization(name: string, industry?: string, logo?: string) {
    const { user, error: userError } = await authService.getCurrentUser();
    if (userError || !user) {
      return { data: null, error: userError };
    }

    // יצירת הארגון
    const { data, error } = await supabase
      .from('organizations')
      .insert({
        name,
        industry,
        logo,
        owner_id: user.id,
      })
      .select()
      .single();

    if (error || !data) {
      return { data: null, error };
    }

    // עדכון ה-org_id בפרופיל המשתמש
    await profileService.updateProfile({ org_id: (data as any).id });

    return { data, error: null };
  },
};

export default {
  supabase,
  authService,
  profileService,
  permissionService,
  leadService,
  organizationService
}; 